import { BaseModel } from "./baseModel";
import { ISOString } from "../isoString";

export type ClaimStatus =
  | "Pending"
  | "Under Review"
  | "Awaiting"
  | "Approved"
  | "Rejected"
  | "Partial"
  | "Negotiated"
  | "Canceled";

export type ClaimIssueType = "damage" | "discrepancy";

export type DamageClaimType = "Physical Damage" | "Expired" | "Wrong Product" | "Missing Items" | "Other";

export type DiscrepancyClaimType =
  | "Quantity Shortage"
  | "Quantity Overage"
  | "Wrong Product Received"
  | "Missing Items"
  | "Quality Issue"
  | "Pricing Discrepancy"
  | "Other";

export type ClaimType = DamageClaimType | DiscrepancyClaimType;

export type ClaimUrgencyLevel = "Low" | "Medium" | "High";

export type WarehouseActionType = "Dispose" | "Add to Samples" | "Other";

// Constants for claim type arrays
export const DAMAGE_CLAIM_TYPES: readonly DamageClaimType[] = [
  "Physical Damage",
  "Expired",
  "Wrong Product",
  "Missing Items",
  "Other"
] as const;

export const DISCREPANCY_CLAIM_TYPES: readonly DiscrepancyClaimType[] = [
  "Quantity Shortage",
  "Quantity Overage",
  "Wrong Product Received",
  "Missing Items",
  "Quality Issue",
  "Pricing Discrepancy",
  "Other"
] as const;

export const CLAIM_URGENCY_LEVELS: readonly ClaimUrgencyLevel[] = ["Low", "Medium", "High"] as const;

export interface ClaimProduct {
  productId: string;
  productName: string;
  sku: string;
  barcode?: string;
  quantity: number;
  claimQuantity: number;
  unitPrice: number;
}

export interface ClaimNonOrderProduct {
  productId: string;
  productName: string;
  sku: string;
  barcode?: string;
  claimQuantity: number;
  unitPrice: number;
  extraInfo?: string;
}

export interface ClaimDetail {
  claimType: ClaimType;
  urgencyLevel: ClaimUrgencyLevel;
  detailedDescription: string;
  nonOrderProducts?: ClaimNonOrderProduct[];
}

export interface Claim extends BaseModel {
  purchaseOrderId: string;
  vendorName: string;
  senderEmail: string;
  issueType: ClaimIssueType;
  products: ClaimProduct[];
  claimDetails: ClaimDetail[];
  warehouseLocation: string;
  status: ClaimStatus;
  submittedByEmail?: string;
  hasImages?: boolean;
  claimPrice: number;
  createdAt: ISOString;
  updatedAt?: ISOString;
  // Status tracking fields
  statusChangedAt?: ISOString;
  // Audit fields
  approvedBy?: string;
  approvedAt?: ISOString;
  rejectedBy?: string;
  rejectedAt?: ISOString;
  rejectionReason?: string;
  // Confirmation fields
  confirmationMessage?: string;
  confirmedBy?: string;
  confirmedAt?: ISOString;
  hasCreditFile?: boolean;
  // Finalization fields
  finalizedBy?: string;
  finalizedAt?: ISOString;
  // Warehouse action (damage claims)
  warehouseAction?: WarehouseActionType;
  warehouseActionNotes?: string;
}
